import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { MagicBackground } from '../components/MagicBackground';
import { Order, Appointment, Vendor } from '../types';
import { format, parseISO, isToday, isPast, isFuture } from 'date-fns';

export default function Queue() {
  const { vendorId: paramVendorId } = useParams<{ vendorId?: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedItem, setSelectedItem] = useState<{ type: 'order' | 'appointment'; item: Order | Appointment } | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isEmployeeAccess, setIsEmployeeAccess] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);

  useEffect(() => {
    // Check if this is employee access (via PIN)
    if (paramVendorId) {
      const authKey = `queue_auth_${paramVendorId}`;
      const pinKey = `queue_pin_${paramVendorId}`;
      const isAuth = sessionStorage.getItem(authKey) === 'true';
      const storedPin = sessionStorage.getItem(pinKey);
      
      if (isAuth && storedPin) {
        setIsEmployeeAccess(true);
        verifyPin(paramVendorId, storedPin);
      } else {
        // Redirect to PIN entry
        navigate(`/queue/${paramVendorId}/auth`);
        return;
      }
    } else {
      // Regular authenticated access
      loadData();
    }
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      if (autoRefresh && (pinVerified || !paramVendorId)) {
        loadData();
      }
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramVendorId, autoRefresh, pinVerified]);

  const verifyPin = async (vid: string, pin: string) => {
    try {
      // Get vendor info from sessionStorage (stored during PIN verification in QueueAuth)
      const vendorInfoStr = sessionStorage.getItem(`queue_vendor_${vid}`);
      if (vendorInfoStr) {
        const vendorInfo = JSON.parse(vendorInfoStr);
        if (vendorInfo.verified) {
          setPinVerified(true);
          loadData(); // loadData will use the stored vendor info
          return;
        }
      }
      
      // If not in sessionStorage, redirect to auth page
      navigate(`/queue/${vid}/auth`);
    } catch (error) {
      console.error('Error verifying PIN:', error);
      navigate(`/queue/${vid}/auth`);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load vendor
      let vendorData: Vendor;
      if (paramVendorId && isEmployeeAccess) {
        // For employee access, get vendor info from sessionStorage (set during PIN verification)
        const vendorInfoStr = sessionStorage.getItem(`queue_vendor_${paramVendorId}`);
        if (vendorInfoStr) {
          const vendorInfo = JSON.parse(vendorInfoStr);
          vendorData = {
            vendorId: vendorInfo.vendorId,
            businessName: vendorInfo.businessName,
            businessType: vendorInfo.businessType || 'order',
            email: '',
            phone: '',
            timezone: 'America/New_York',
            subscriptionTier: 'free',
            status: 'active',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          } as Vendor;
        } else {
          // Fallback: try regular endpoint
          try {
            vendorData = await apiService.getVendor();
          } catch (error) {
            // If not authenticated, create minimal vendor object
            vendorData = {
              vendorId: paramVendorId,
              businessName: 'Business',
              email: '',
              phone: '',
              timezone: 'America/New_York',
              subscriptionTier: 'free',
              status: 'active',
              businessType: 'order',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            } as Vendor;
          }
        }
      } else {
        vendorData = await apiService.getVendor();
      }
      setVendor(vendorData);

      const today = new Date().toISOString().split('T')[0];

      // Load orders (only if authenticated or employee access)
      try {
        let ordersData: Order[] = [];
        if (isEmployeeAccess && paramVendorId) {
          // Use employee-specific endpoint
          const storedPin = sessionStorage.getItem(`queue_pin_${paramVendorId}`);
          if (storedPin) {
            ordersData = await apiService.getOrdersForEmployee(paramVendorId, storedPin, {
              date: today,
              status: 'pending,paid,completed'
            });
          }
        } else {
          // Use regular authenticated endpoint
          ordersData = await apiService.getOrders({ 
            date: today,
            status: 'pending,paid,completed'
          });
        }
        setOrders(ordersData);
      } catch (error) {
        console.error('Error loading orders:', error);
        // For employee access, orders might not be accessible - that's OK
        if (!isEmployeeAccess) {
          throw error;
        }
      }

      // Load appointments if business supports them
      if (vendorData?.businessType === 'appointment' || vendorData?.businessType === 'hybrid') {
        try {
          let appointmentsData: Appointment[] = [];
          if (isEmployeeAccess && paramVendorId) {
            // Use employee-specific endpoint
            const storedPin = sessionStorage.getItem(`queue_pin_${paramVendorId}`);
            if (storedPin) {
              appointmentsData = await apiService.getAppointmentsForEmployee(paramVendorId, storedPin, {
                date: today
              });
            }
          } else {
            // Use regular authenticated endpoint
            appointmentsData = await apiService.getAppointments({ date: today });
          }
          setAppointments(appointmentsData);
        } catch (error) {
          console.error('Error loading appointments:', error);
          // For employee access, appointments might not be accessible - that's OK
          if (!isEmployeeAccess) {
            throw error;
          }
        }
      }
    } catch (error) {
      console.error('Error loading queue data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (type: 'order' | 'appointment', id: string, newStatus: string) => {
    try {
      if (isEmployeeAccess && paramVendorId) {
        // Use employee-specific endpoints
        const storedPin = sessionStorage.getItem(`queue_pin_${paramVendorId}`);
        if (!storedPin) {
          navigate(`/queue/${paramVendorId}/auth`);
          return;
        }
        
        if (type === 'order') {
          await apiService.updateOrderForEmployee(paramVendorId, id, storedPin, { status: newStatus as any });
        } else {
          await apiService.updateAppointmentForEmployee(paramVendorId, id, storedPin, { status: newStatus as any });
        }
      } else {
        // Use regular authenticated endpoints
        if (type === 'order') {
          await apiService.updateOrder(id, { status: newStatus as any });
        } else {
          await apiService.updateAppointment(id, { status: newStatus as any });
        }
      }
      await loadData();
    } catch (error: any) {
      console.error('Error updating status:', error);
      // For employee access, if unauthorized, might need different handling
      if (isEmployeeAccess && error.response?.status === 401 && paramVendorId) {
        // Re-authenticate
        navigate(`/queue/${paramVendorId}/auth`);
      }
    }
  };

  const handleViewDetails = (type: 'order' | 'appointment', item: Order | Appointment) => {
    setSelectedItem({ type, item });
    setDetailDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
      case 'scheduled':
        return 'warning';
      case 'paid':
      case 'confirmed':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
      case 'refunded':
        return 'error';
      default:
        return 'default';
    }
  };

  const pendingOrders = orders.filter(o => o.status === 'pending' || (o.status === 'paid' && o.paymentStatus === 'paid'));
  const completedOrders = orders.filter(o => o.status === 'completed');
  
  const todayAppointments = appointments.filter(a => {
    const apptDate = new Date(a.startTime);
    return isToday(apptDate) || isFuture(apptDate);
  });
  const upcomingAppointments = todayAppointments.filter(a => {
    const apptDate = new Date(a.startTime);
    return (isFuture(apptDate) && a.status === 'scheduled') || a.status === 'confirmed';
  });
  const pastAppointments = todayAppointments.filter(a => {
    const apptDate = new Date(a.startTime);
    return isPast(apptDate) && a.status !== 'completed' && a.status !== 'cancelled';
  });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        }}
      >
        <MagicBackground />
        <CircularProgress sx={{ color: '#667eea', position: 'relative', zIndex: 1 }} size={60} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        minHeight: '100vh',
        position: 'relative',
        py: 4,
      }}
    >
      <MagicBackground />
      
      <Box maxWidth="xl" sx={{ mx: 'auto', px: 3, position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography
              variant="h3"
              fontWeight="bold"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Queue
            </Typography>
            <Typography variant="body1" color="rgba(255,255,255,0.7)">
              {format(new Date(), 'EEEE, MMMM d, yyyy')}
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)',
                '&:hover': { borderColor: '#667eea', bgcolor: 'rgba(102, 126, 234, 0.1)' },
              }}
            >
              Refresh
            </Button>
            <Chip
              label={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
              onClick={() => setAutoRefresh(!autoRefresh)}
              color={autoRefresh ? 'primary' : 'default'}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Orders Section */}
          {(vendor?.businessType === 'order' || vendor?.businessType === 'hybrid') && (
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <RestaurantIcon sx={{ color: '#667eea', fontSize: 32 }} />
                    <Box>
                      <Typography variant="h5" fontWeight="bold" color="white">
                        Orders
                      </Typography>
                      <Typography variant="body2" color="rgba(255,255,255,0.7)">
                        {pendingOrders.length} pending • {completedOrders.length} completed today
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                  {/* Pending Orders */}
                  {pendingOrders.length === 0 ? (
                    <Box textAlign="center" py={4}>
                      <Typography variant="body2" color="rgba(255,255,255,0.7)">
                        No pending orders
                      </Typography>
                    </Box>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={2}>
                      {pendingOrders.map((order) => (
                        <Card
                          key={order.orderId}
                          sx={{
                            bgcolor: 'rgba(0, 0, 0, 0.2)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            '&:hover': { borderColor: '#667eea' },
                          }}
                        >
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                              <Box>
                                <Typography variant="h6" color="white" fontWeight="bold">
                                  Order #{order.orderId.substring(0, 8)}
                                </Typography>
                                <Typography variant="body2" color="rgba(255,255,255,0.7)">
                                  {order.customerName || 'Customer'}
                                </Typography>
                                <Typography variant="body2" color="rgba(255,255,255,0.7)">
                                  {format(parseISO(order.createdAt), 'h:mm a')}
                                </Typography>
                              </Box>
                              <Chip
                                label={order.status}
                                color={getStatusColor(order.status) as any}
                                size="small"
                              />
                            </Box>

                            <Box mb={2}>
                              <Typography variant="body2" color="rgba(255,255,255,0.9)" fontWeight="bold" mb={1}>
                                Items:
                              </Typography>
                              {order.items.map((item, idx) => (
                                <Typography key={idx} variant="body2" color="rgba(255,255,255,0.7)">
                                  {item.quantity}x {item.name} - ${(item.total / 100).toFixed(2)}
                                </Typography>
                              ))}
                              <Typography variant="body1" color="white" fontWeight="bold" mt={1}>
                                Total: ${(order.total / 100).toFixed(2)}
                              </Typography>
                            </Box>

                            <Box display="flex" gap={1}>
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails('order', order)}
                                sx={{ color: '#667eea' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              {order.status === 'paid' && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => handleStatusUpdate('order', order.orderId, 'completed')}
                                  sx={{
                                    bgcolor: '#43e97b',
                                    '&:hover': { bgcolor: '#38d16a' },
                                  }}
                                >
                                  Mark Ready
                                </Button>
                              )}
                              {order.status === 'pending' && order.paymentStatus === 'paid' && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => handleStatusUpdate('order', order.orderId, 'completed')}
                                  sx={{
                                    bgcolor: '#43e97b',
                                    '&:hover': { bgcolor: '#38d16a' },
                                  }}
                                >
                                  Mark Ready
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Appointments Section */}
          {(vendor?.businessType === 'appointment' || vendor?.businessType === 'hybrid') && (
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <EventIcon sx={{ color: '#667eea', fontSize: 32 }} />
                    <Box>
                      <Typography variant="h5" fontWeight="bold" color="white">
                        Appointments
                      </Typography>
                      <Typography variant="body2" color="rgba(255,255,255,0.7)">
                        {upcomingAppointments.length} upcoming • {pastAppointments.length} past
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                  {/* Upcoming Appointments */}
                  {upcomingAppointments.length === 0 && pastAppointments.length === 0 ? (
                    <Box textAlign="center" py={4}>
                      <Typography variant="body2" color="rgba(255,255,255,0.7)">
                        No appointments today
                      </Typography>
                    </Box>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={2}>
                      {[...upcomingAppointments, ...pastAppointments].map((appointment) => (
                        <Card
                          key={appointment.appointmentId}
                          sx={{
                            bgcolor: 'rgba(0, 0, 0, 0.2)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            '&:hover': { borderColor: '#667eea' },
                          }}
                        >
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                              <Box>
                                <Typography variant="h6" color="white" fontWeight="bold">
                                  {appointment.customerName}
                                </Typography>
                                <Typography variant="body2" color="rgba(255,255,255,0.7)">
                                  {appointment.serviceName}
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1} mt={1}>
                                  <AccessTimeIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.7)' }} />
                                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                                    {format(parseISO(appointment.startTime), 'h:mm a')}
                                  </Typography>
                                  {appointment.duration && (
                                    <Typography variant="body2" color="rgba(255,255,255,0.7)">
                                      • {appointment.duration} min
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              <Chip
                                label={appointment.status}
                                color={getStatusColor(appointment.status) as any}
                                size="small"
                              />
                            </Box>

                            {appointment.price && (
                              <Typography variant="body2" color="white" mb={2}>
                                ${(appointment.price / 100).toFixed(2)}
                              </Typography>
                            )}

                            <Box display="flex" gap={1}>
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails('appointment', appointment)}
                                sx={{ color: '#667eea' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              {(appointment.status === 'scheduled' || appointment.status === 'confirmed') && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => handleStatusUpdate('appointment', appointment.appointmentId, 'completed')}
                                  sx={{
                                    bgcolor: '#43e97b',
                                    '&:hover': { bgcolor: '#38d16a' },
                                  }}
                                >
                                  Complete
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1a1f3a',
            color: 'white',
          },
        }}
      >
        <DialogTitle>
          {selectedItem?.type === 'order' ? 'Order Details' : 'Appointment Details'}
        </DialogTitle>
        <DialogContent>
          {selectedItem?.type === 'order' && (
            <Box>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={2}>
                Order ID: {(selectedItem.item as Order).orderId}
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={2}>
                Customer: {(selectedItem.item as Order).customerName || 'N/A'}
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={2}>
                Created: {format(parseISO((selectedItem.item as Order).createdAt), 'PPpp')}
              </Typography>
              {(selectedItem.item as Order).notes && (
                <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={2}>
                  Notes: {(selectedItem.item as Order).notes}
                </Typography>
              )}
            </Box>
          )}
          {selectedItem?.type === 'appointment' && (
            <Box>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={2}>
                Customer: {(selectedItem.item as Appointment).customerName}
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={2}>
                Service: {(selectedItem.item as Appointment).serviceName}
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={2}>
                Time: {format(parseISO((selectedItem.item as Appointment).startTime), 'PPpp')}
              </Typography>
              {(selectedItem.item as Appointment).notes && (
                <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={2}>
                  Notes: {(selectedItem.item as Appointment).notes}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)} sx={{ color: 'white' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

